import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Mic,
  ArrowLeft,
  CheckCircle,
  Clock,
  Calendar,
  Video,
  User,
  Mail,
  Search,
  Loader2,
  Award,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  Filter
} from "lucide-react";

export default function PodcastManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedClient, setExpandedClient] = useState(null);
  const [completingMeeting, setCompletingMeeting] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState("");
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  useEffect(() => {
    if (user?.role !== "consultant") {
      navigate("/dashboard");
      return;
    }
    fetchEnrollments();
  }, [user]);

  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/podcast/consultant/enrollments`);
      if (response.ok) {
        const data = await response.json();
        setEnrollments(data);
      } else {
        toast.error("Failed to load enrollments");
      }
    } catch (_error) {
      console.error("Error fetching enrollments:", _error);
      toast.error("Failed to load enrollments");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteMeeting = async () => {
    if (!selectedMeeting) return;
    
    setCompletingMeeting(`${selectedMeeting.clientId}-${selectedMeeting.week}`);
    try {
      const response = await authFetch(
        `${API}/podcast/meeting/${selectedMeeting.week}/complete?client_id=${selectedMeeting.clientId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes: meetingNotes })
        }
      );
      
      if (response.ok) {
        toast.success(`Week ${selectedMeeting.week} meeting marked as complete!`);
        setShowNotesDialog(false);
        setMeetingNotes("");
        setSelectedMeeting(null);
        fetchEnrollments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to complete meeting");
      }
    } catch (_error) {
      toast.error("Failed to complete meeting");
    } finally {
      setCompletingMeeting(null);
    }
  };

  const openMeetingDialog = (clientId, week, clientName) => {
    setSelectedMeeting({ clientId, week, clientName });
    setMeetingNotes("");
    setShowNotesDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "graduated": return "bg-green-100 text-green-700";
      case "intake_pending": return "bg-yellow-100 text-yellow-700";
      default: return "bg-blue-100 text-blue-700";
    }
  };

  const getMeetingStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "scheduled": return <Clock className="w-4 h-4 text-blue-600" />;
      default: return <Calendar className="w-4 h-4 text-neutral-400" />;
    }
  };

  const filteredEnrollments = enrollments.filter(e => {
    const matchesSearch = 
      e.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "graduated") return matchesSearch && e.status === "graduated";
    if (filterStatus === "active") return matchesSearch && e.status !== "graduated" && e.status !== "intake_pending";
    if (filterStatus === "intake") return matchesSearch && e.status === "intake_pending";
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-neutral-200" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Mic className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-neutral-900">Podcast Academy Management</span>
              </div>
            </div>
            <Badge variant="outline" className="text-purple-600">
              {enrollments.length} Enrollments
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-purple-600">{enrollments.length}</div>
              <p className="text-sm text-neutral-500">Total Enrolled</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">
                {enrollments.filter(e => e.status === "graduated").length}
              </div>
              <p className="text-sm text-neutral-500">Graduated</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {enrollments.filter(e => e.status !== "graduated" && e.status !== "intake_pending").length}
              </div>
              <p className="text-sm text-neutral-500">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-amber-600">
                {enrollments.reduce((acc, e) => {
                  const meetings = e.meetings || {};
                  return acc + Object.values(meetings).filter(m => m.status === "scheduled").length;
                }, 0)}
              </div>
              <p className="text-sm text-neutral-500">Pending Meetings</p>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-enrollments"
            />
          </div>
          <div className="flex gap-2">
            {["all", "active", "graduated", "intake"].map(status => (
              <Button
                key={status}
                variant={filterStatus === status ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={filterStatus === status ? "bg-purple-600" : ""}
              >
                {status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Enrollments List */}
        <div className="space-y-4">
          {filteredEnrollments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Mic className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                <p className="text-neutral-500">No enrollments found</p>
              </CardContent>
            </Card>
          ) : (
            filteredEnrollments.map((enrollment) => (
              <Card key={enrollment.enrollment_id} className="overflow-hidden">
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                  onClick={() => setExpandedClient(expandedClient === enrollment.enrollment_id ? null : enrollment.enrollment_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold">
                      {enrollment.client_name?.charAt(0) || "?"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-neutral-900">{enrollment.client_name}</h3>
                        <Badge className={getStatusColor(enrollment.status)}>
                          {enrollment.status === "graduated" ? "Graduated" : 
                           enrollment.status === "intake_pending" ? "Intake Pending" : 
                           `Week ${enrollment.current_week}`}
                        </Badge>
                      </div>
                      <p className="text-sm text-neutral-500">{enrollment.client_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Meeting progress indicator */}
                    <div className="hidden sm:flex items-center gap-1">
                      {[1, 2, 3, 4, 5, 6].map(week => {
                        const meetingStatus = enrollment.meetings?.[week]?.status || "pending";
                        return (
                          <div 
                            key={week} 
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                              ${meetingStatus === "completed" ? "bg-green-100 text-green-700" : 
                                meetingStatus === "scheduled" ? "bg-blue-100 text-blue-700" : 
                                "bg-neutral-100 text-neutral-400"}`}
                          >
                            {week}
                          </div>
                        );
                      })}
                    </div>
                    {expandedClient === enrollment.enrollment_id ? (
                      <ChevronDown className="w-5 h-5 text-neutral-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-neutral-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Meeting Details */}
                {expandedClient === enrollment.enrollment_id && (
                  <div className="border-t border-neutral-200 bg-neutral-50 p-4">
                    <h4 className="font-medium text-neutral-700 mb-4 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Weekly Consultation Meetings
                    </h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[1, 2, 3, 4, 5, 6].map(week => {
                        const meeting = enrollment.meetings?.[week] || { status: "pending" };
                        const isScheduled = meeting.status === "scheduled";
                        const isCompleted = meeting.status === "completed";
                        const isUnlocked = week <= enrollment.current_week;
                        
                        return (
                          <div 
                            key={week}
                            className={`p-3 rounded-lg border ${
                              isCompleted ? "bg-green-50 border-green-200" :
                              isScheduled ? "bg-blue-50 border-blue-200" :
                              isUnlocked ? "bg-white border-neutral-200" :
                              "bg-neutral-100 border-neutral-200 opacity-50"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-neutral-700">Week {week}</span>
                              {getMeetingStatusIcon(meeting.status)}
                            </div>
                            <p className="text-xs text-neutral-500 mb-2">
                              {isCompleted ? `Completed ${meeting.completed_at ? new Date(meeting.completed_at).toLocaleDateString() : ""}` :
                               isScheduled ? "Scheduled - awaiting completion" :
                               isUnlocked ? "Not yet scheduled" :
                               "Week not unlocked"}
                            </p>
                            {isScheduled && !isCompleted && (
                              <Button
                                size="sm"
                                className="w-full bg-green-600 hover:bg-green-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMeetingDialog(enrollment.user_id, week, enrollment.client_name);
                                }}
                                disabled={completingMeeting === `${enrollment.user_id}-${week}`}
                                data-testid={`complete-meeting-${week}`}
                              >
                                {completingMeeting === `${enrollment.user_id}-${week}` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Mark Complete
                                  </>
                                )}
                              </Button>
                            )}
                            {meeting.consultant_notes && (
                              <p className="text-xs text-neutral-600 mt-2 italic">
                                Note: {meeting.consultant_notes}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-200">
                      {enrollment.status === "graduated" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`${API}/podcast/certificate/${enrollment.user_id}`, '_blank')}
                        >
                          <Award className="w-4 h-4 mr-2" />
                          Download Certificate
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/consultant/client/${enrollment.user_id}`)}
                      >
                        <User className="w-4 h-4 mr-2" />
                        View Client
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Meeting Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Week {selectedMeeting?.week} Meeting</DialogTitle>
            <DialogDescription>
              Mark the consultation meeting with {selectedMeeting?.clientName} as complete.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-neutral-700">Meeting Notes (Optional)</label>
            <Textarea
              placeholder="Add notes about the meeting, key takeaways, or action items..."
              value={meetingNotes}
              onChange={(e) => setMeetingNotes(e.target.value)}
              rows={4}
              className="mt-2"
              data-testid="meeting-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteMeeting}
              disabled={completingMeeting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-complete-meeting"
            >
              {completingMeeting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Complete Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
